import { useEffect, useState } from 'react';
import { apiGet, apiPut, apiDelete, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { Switch } from '../components/FormCheck';
import { ServiceCardApprovalsPanel } from '../components/ServiceCardApprovalsPanel';

/**
 * GET /admin/service-cards response shape (backend PR #380, ServiceCardOverrideService.
 * getEffectiveCards). `estimatedTurnaroundEn/Es` + `legacyIds` added for ADM-50/51
 * (docs/admin-portal.md §17.7) — this is the same data `app/constants/serviceMatrix.ts`
 * now reads at runtime via `GET /service-cards/effective` instead of its own hardcoded copy.
 *
 * One row per `(category, service)` catalog offering, not per service type: `id` is the
 * composite `${category}:${serviceType}` key. Duration/turnaround stopped being a single
 * value shared across every category of a service (a LIVE session's length and a pure
 * ASYNC written response's SLA are unrelated numbers) — `durationMinutes` is `undefined`
 * for a category with no session concept (e.g. a pure ASYNC written review).
 */
interface ServiceCard {
  id: string;
  serviceType: string;
  category: string;
  categories: string[];
  legacyIds: string[];
  basePriceUsd: number;
  durationMinutes: number | undefined;
  estimatedTurnaroundEn: string;
  estimatedTurnaroundEs: string;
  enabled: boolean;
  isOverridden: boolean;
  priceOverridden: boolean;
  turnaroundOverridden: boolean;
  durationOverridden: boolean;
  /**
   * The catalog base each field reverts to when its override is cleared —
   * distinct from the fields above, which are the *effective* values. Lets the
   * table show what an "Overridden" badge is actually overriding.
   */
  base: {
    priceUsd: number;
    durationMinutes: number | undefined;
    turnaroundEn: string;
    turnaroundEs: string;
  };
}

interface UpdateServiceCardBody {
  basePriceUsd?: number;
  durationMinutes?: number;
  enabled?: boolean;
  estimatedTurnaroundEn?: string;
  estimatedTurnaroundEs?: string;
}

/**
 * ADM ops — Service Cards (Task C). Duration/base price/enabled per ServiceType, backed by
 * PUT /admin/service-cards/:id (a per-field partial update, not full-replace).
 *
 * Follows the RolesPermissionsPage matrix pattern's two hard-won fixes: (1) only the *first*
 * load gates the table — a reload from a save must not unmount every row and blank the page,
 * since the per-row busy state is what should show in-flight, and (2) the enabled toggle uses
 * optimistic local state so the checkbox doesn't visibly "un-click" itself while the PUT is
 * in flight (a controlled checkbox re-renders from stale server data until the round trip
 * lands otherwise).
 */
export function ServiceCardsPage() {
  const { data: cards, loading, error, reload } = useApiResource(
    () => apiGet<ServiceCard[]>('/admin/service-cards'),
    [],
  );

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Optimistic enabled state, keyed by card id. Cleared once `cards` confirms the value, or
  // immediately on a failed PUT (nothing changed server-side).
  const [pendingEnabled, setPendingEnabled] = useState<Record<string, boolean>>({});

  // Uncommitted duration/price edits, keyed by card id — a row-edit draft, not sent until
  // "Save" is clicked.
  const [drafts, setDrafts] = useState<
    Record<string, { basePriceUsd: string; durationMinutes: string; estimatedTurnaroundEn: string; estimatedTurnaroundEs: string }>
  >({});

  useEffect(() => {
    if (!cards || Object.keys(pendingEnabled).length === 0) return;
    setPendingEnabled((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of Object.keys(prev)) {
        const card = cards.find((c) => c.id === id);
        if (card && card.enabled === prev[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [cards, pendingEnabled]);

  const updateCard = async (id: string, updates: UpdateServiceCardBody) => {
    setBusyId(id);
    setActionError(null);
    try {
      await apiPut(`/admin/service-cards/${encodeURIComponent(id)}`, updates);
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Service card update failed');
      if ('enabled' in updates) {
        setPendingEnabled((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Clear this card's overrides so every field falls back to the catalog base.
   * Overrides used to be one-way — the API could only set values — so a typed
   * price could not be undone from the portal at all.
   */
  const resetCard = async (card: ServiceCard) => {
    setBusyId(card.id);
    setActionError(null);
    try {
      await apiDelete(`/admin/service-cards/${encodeURIComponent(card.id)}/override`);
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Service card reset failed');
    } finally {
      setBusyId(null);
    }
  };

  const toggleEnabled = (card: ServiceCard, checked: boolean) => {
    setPendingEnabled((prev) => ({ ...prev, [card.id]: checked }));
    void updateCard(card.id, { enabled: checked });
  };

  const editDraft = (card: ServiceCard) =>
    drafts[card.id] ?? {
      basePriceUsd: String(card.basePriceUsd),
      durationMinutes: card.durationMinutes === undefined ? '' : String(card.durationMinutes),
      estimatedTurnaroundEn: card.estimatedTurnaroundEn,
      estimatedTurnaroundEs: card.estimatedTurnaroundEs,
    };

  const startEdit = (card: ServiceCard) => {
    setDrafts((prev) => ({
      ...prev,
      [card.id]: {
        basePriceUsd: String(card.basePriceUsd),
        durationMinutes: card.durationMinutes === undefined ? '' : String(card.durationMinutes),
        estimatedTurnaroundEn: card.estimatedTurnaroundEn,
        estimatedTurnaroundEs: card.estimatedTurnaroundEs,
      },
    }));
  };

  const cancelEdit = (id: string) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const saveEdit = async (card: ServiceCard) => {
    const draft = drafts[card.id];
    if (!draft) return;
    const basePriceUsd = Number(draft.basePriceUsd);
    if (!Number.isFinite(basePriceUsd) || basePriceUsd < 0) {
      setActionError('Base price must be a number >= 0');
      return;
    }
    // Blank means "no session for this category" (e.g. a pure ASYNC written review) —
    // left out of the update rather than validated as a number.
    let durationMinutes: number | undefined;
    if (draft.durationMinutes.trim() !== '') {
      durationMinutes = Number(draft.durationMinutes);
      if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
        setActionError('Duration must be a number > 0, or blank if this category has no session');
        return;
      }
    }
    if (!draft.estimatedTurnaroundEn.trim() || !draft.estimatedTurnaroundEs.trim()) {
      setActionError('Turnaround estimate (English and Spanish) must not be empty');
      return;
    }
    await updateCard(card.id, {
      basePriceUsd,
      ...(durationMinutes !== undefined ? { durationMinutes } : {}),
      estimatedTurnaroundEn: draft.estimatedTurnaroundEn,
      estimatedTurnaroundEs: draft.estimatedTurnaroundEs,
    });
    cancelEdit(card.id);
  };

  // Only the first load blocks the whole table — every save calls reload(), which flips
  // `loading` back to true; gating on that would unmount every row (and its in-flight state)
  // on every single edit.
  const showLoadingState = !cards && loading;

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Service Cards</h1>
          <p>Duration, base price, and availability per service type. Changes take effect immediately, no redeploy.</p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      {/* F4 — changes queued for a second admin. Renders nothing when the queue is
          empty, which is the normal state. */}
      <ServiceCardApprovalsPanel />

      <DataTable
        id="service-cards"
        rows={cards}
        loading={showLoadingState}
        empty="No service cards found."
        noun="cards"
        getRowKey={(card) => card.id}
        columns={[
          { key: 'serviceType', header: 'Service type', value: (card) => card.serviceType, filter: 'select' },
          { key: 'category', header: 'Category', value: (card) => card.category, filter: 'select' },
          {
            key: 'basePrice',
            header: 'Base price (USD)',
            value: (card) => card.basePriceUsd,
            align: 'right',
            render: (card) => {
              const draft = drafts[card.id];
              const isEditing = !!draft;
              return (
                <>
                  {isEditing ? (
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={draft.basePriceUsd}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [card.id]: { ...editDraft(card), basePriceUsd: e.target.value } }))
                      }
                      style={{ width: '6rem' }}
                    />
                  ) : (
                    <>
                      {`$${card.basePriceUsd.toFixed(2)}`}
                      {card.priceOverridden && (
                        <div className="cell-sub" title="Catalog base price">
                          base ${card.base.priceUsd.toFixed(2)}
                        </div>
                      )}
                    </>
                  )}

                </>
              );
            },
          },
          {
            key: 'duration',
            header: 'Duration (min)',
            value: (card) => card.durationMinutes ?? '',
            align: 'right',
            render: (card) => {
              const draft = drafts[card.id];
              const isEditing = !!draft;
              return (
                <>
                  {isEditing ? (
                    <input
                      type="number"
                      min={1}
                      step="1"
                      placeholder="N/A"
                      value={draft.durationMinutes}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [card.id]: { ...editDraft(card), durationMinutes: e.target.value } }))
                      }
                      style={{ width: '5rem' }}
                    />
                  ) : card.durationMinutes === undefined ? (
                    <span title="No live session for this category">N/A</span>
                  ) : (
                    <>
                      {card.durationMinutes}
                      {card.durationOverridden && card.base.durationMinutes !== undefined && (
                        <div className="cell-sub" title="Catalog base duration">
                          base {card.base.durationMinutes}
                        </div>
                      )}
                    </>
                  )}

                </>
              );
            },
          },
          {
            key: 'turnaroundEn',
            header: 'Turnaround (EN)',
            value: (card) => card.estimatedTurnaroundEn,
            render: (card) => {
              const draft = drafts[card.id];
              const isEditing = !!draft;
              return (
                <>
                  {isEditing ? (
                    <input
                      type="text"
                      value={draft.estimatedTurnaroundEn}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [card.id]: { ...editDraft(card), estimatedTurnaroundEn: e.target.value } }))
                      }
                      style={{ width: '8rem' }}
                    />
                  ) : (
                    <>
                      {card.estimatedTurnaroundEn}
                      {card.turnaroundOverridden && (
                        <div className="cell-sub" title="Catalog base turnaround">
                          base {card.base.turnaroundEn}
                        </div>
                      )}
                    </>
                  )}

                </>
              );
            },
          },
          {
            key: 'turnaroundEs',
            header: 'Turnaround (ES)',
            value: (card) => card.estimatedTurnaroundEs,
            render: (card) => {
              const draft = drafts[card.id];
              const isEditing = !!draft;
              return (
                <>
                  {isEditing ? (
                    <input
                      type="text"
                      value={draft.estimatedTurnaroundEs}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [card.id]: { ...editDraft(card), estimatedTurnaroundEs: e.target.value } }))
                      }
                      style={{ width: '8rem' }}
                    />
                  ) : (
                    <>
                      {card.estimatedTurnaroundEs}
                      {card.turnaroundOverridden && (
                        <div className="cell-sub" title="Catalog base turnaround">
                          base {card.base.turnaroundEs}
                        </div>
                      )}
                    </>
                  )}

                </>
              );
            },
          },
          {
            key: 'overridden',
            header: 'Overridden',
            value: (card) => (card.isOverridden ? 'Overridden' : 'Default'),
            filter: 'select',
            render: (card) =>
              card.isOverridden ? (
                <StatusBadge label="Overridden" tone="warn" />
              ) : (
                <StatusBadge label="Default" tone="neutral" />
              ),
          },
          {
            key: 'enabled',
            header: 'Enabled',
            width: '90px',
            render: (card) => (
              <Switch
                checked={card.id in pendingEnabled ? pendingEnabled[card.id]! : card.enabled}
                disabled={busyId === card.id}
                tone="success"
                ariaLabel={`Enable ${card.serviceType}`}
                onChange={(next) => toggleEnabled(card, next)}
              />
            ),
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (card) => {
              const draft = drafts[card.id];
              const isEditing = !!draft;
              return (
                <>
                  <div className="row-actions">
                    {isEditing ? (
                      <>
                        <button className="btn btn-primary" disabled={busyId === card.id} onClick={() => void saveEdit(card)}>
                          Save
                        </button>
                        <button className="btn" disabled={busyId === card.id} onClick={() => cancelEdit(card.id)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn" disabled={busyId === card.id} onClick={() => startEdit(card)}>
                          Edit
                        </button>
                        {card.isOverridden && (
                          <button
                            className="btn"
                            disabled={busyId === card.id}
                            title="Clear overrides and revert to the catalog base"
                            data-testid={`service-card-reset-${card.id}`}
                            onClick={() => void resetCard(card)}
                          >
                            Reset
                          </button>
                        )}
                      </>
                    )}
                  </div>

                </>
              );
            },
          },
        ]}
      />
    </div>
  );
}
